import { mapActions, mapState } from "pinia";

import {
  CanvasEventHandlerMixinStaging,
} from "%/dynamic-component-editor/mixins/canvas-event-handler-mixin-staging";
import { CanvasStyleMixinStaging } from "%/dynamic-component-editor/mixins/canvas-style-mixin-staging";
import { BrandedTextMixinStaging } from "%/dynamic-component-editor/mixins/branded-text-mixin-staging";
import { DragAndDropMixinStaging } from "%/dynamic-component-editor/mixins/drag-and-drop-mixin-staging";
import { ContextualMenuMixinStaging } from "%/dynamic-component-editor/mixins/contextual-menu-mixin-staging";

import { useCollaboratorStore } from "~/stores/collaborator-store";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useConfigurationPanelStore = (...args) => require("%/dynamic-component-editor/stores/configuration-panel-store").useConfigurationPanelStore(...args);
// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);


export const ElementEditMixin = {
  props: {
    identifier: {
      type: String,
      required: false,
    },
    overrides: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    editMode: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  mixins: [
    DragAndDropMixinStaging,
    BrandedTextMixinStaging,
    CanvasStyleMixinStaging,
    CanvasEventHandlerMixinStaging,
    ContextualMenuMixinStaging,
  ],
  computed: {
    ...mapState(useCollaboratorStore, [
      "canEditPublication",
    ]),
    dynamicComponentInstance() {
      return DynamicComponent.get(DynamicElement.get(this.identifier)?.dynamicComponentId);
    },
    isTemplatedComponent() {
      return useDynamicComponentEditorStore().isElementTemplatedComponent(this.identifier);
    },
    elementConfig() {
      return DynamicElement.get(this.identifier)?.elementDef ?? null;
    },
    // Renamed from `configuration` to dodge a Vue prop/computed
    // collision — `DynamicElementMixin` (and `LayoutElementMixinProduction`)
    // declares a `configuration` PROP for callers that hand in pre-built
    // element data (e.g. the preview grid + the legacy render-engine).
    // A same-named computed silently lost to the prop on every read in
    // the editor canvas, so the menu / contextual checks below were
    // querying `{}` instead of the live element entity.
    liveConfiguration() {
      return this.dynamicComponentInstance.getElement(this.identifier);
    },
    elementIsDeprecated() {
      if (!this.elementConfig) {
        return false;
      }
      return this.elementConfig.deprecated;
    },
    isRootLayout() {
      return this.dynamicComponentInstance.rootElementId === this.identifier;
    },
    isLayout() {
      return (this.$options.elementPanelSettings || {}).elementType === "layout";
    },
  },
  methods: {
    ...mapActions(useConfigurationPanelStore, [
      "createDynamicComponentFromIdentifier",
    ]),
    ...mapActions(useDynamicComponentEditorStore, [
      "setEditorElementConfig",
    ]),
  },
};
